import React, { useEffect, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { XMarkIcon, CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import { homeScreenData } from "./useHook";
import Swal from "sweetalert2";

function DataManagement() {
  const { getHomeScreenData, postHomeScreenData, deleteHomeScreenData } =
    homeScreenData();

  const location = useLocation();
  const { value: originalValue } = location.state || {};

  const valueMapping = {
    "ourProjects": "projects",
    "estimations": "construction"
  };

  const value = valueMapping[originalValue] || originalValue;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [homeScreen, setHomeScreen] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState("");
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setImagePreview(null);
    setSelectedImage(null);
    setImageError("");
  };

  useEffect(() => {
    getHomeScreenData(setHomeScreen);
  }, []);

  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === 1600 && img.height === 900) {
          resolve(true);
        } else {
          reject(
            new Error(
              `Image dimensions must be exactly 1600x900 pixels. Current dimensions: ${img.width}x${img.height}`
            )
          );
        }
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError("");

    if (!file.type.startsWith("image/")) {
      setImageError("Please select a valid image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image size must be less than 5MB.");
      return;
    }

    try {
      await validateImageDimensions(file);

      setSelectedImage(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setImageError(error.message);
      setSelectedImage(null);
      setImagePreview(null);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageError("");
  };

  const handleSubmit = async () => {
    const form = document.querySelector("#homeScreenForm");
    if (!form) return;

    const formData = new FormData(form);

    if (selectedImage) {
      formData.set("image", selectedImage);
    }

    const data = Object.fromEntries(formData.entries());

    if (!selectedImage) {
      setImageError("Image is required.");
      return;
    }

    if (!data.link) {
      Swal.fire("Error!", "Link is required.", "error");
      return;
    }

    const normalizedValue = value?.trim().toLowerCase();

    const backendFieldMapping = {
      "construction": "estimations",
      "projects": "ourProjects"
    };

    const categories = [
      "houseDesign",
      "construction",
      "shop",
      "projects",
      "frontElevation",
      "interiorDesign",
      "blog",
    ];

    let categoryData = {};

    const matchingCategory = categories.find(
      (cat) => cat.toLowerCase() === normalizedValue
    );

    if (matchingCategory) {
      const backendFieldName = backendFieldMapping[matchingCategory] || matchingCategory;
      categoryData[backendFieldName] = true;
    } else {
      console.warn("No matching category found for:", normalizedValue);
    }

    const finalData = { ...data, ...categoryData };

    try {
      await postHomeScreenData(finalData);
      await getHomeScreenData(setHomeScreen);
      closeModal();
      Swal.fire("Success!", "Item added successfully.", "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Something went wrong.", "error");
    }
  };

  const formatCategoryName = (name) => {
    return name
      .replace(/([A-Z])/g, " $1")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteHomeScreenData(id);
        await getHomeScreenData(setHomeScreen);
        Swal.fire("Deleted!", "Your item has been deleted.", "success");
      } catch (error) {
        console.error(error);
        Swal.fire("Error!", "Something went wrong.", "error");
      }
    }
  };
  let categoryData = [];
  if (homeScreen?.data?.[value]) {
    categoryData = homeScreen.data[value];
  } else if (homeScreen?.[value]) {
    categoryData = homeScreen[value];
  } else if (Array.isArray(homeScreen?.data)) {
    categoryData = homeScreen.data;
  } else {
    console.log("No data found for value:", value);
  }
  return (
    <div className="bg-gray-100 p-4 min-h-screen">
      <h2 className="text-2xl font-bold mb-6 text-center">
        Manage {formatCategoryName(value)}
      </h2>

      {categoryData.length === 0 ? (
        <p className="text-center text-gray-600">No data available.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categoryData.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow overflow-hidden"
            >
              <img
                src={item.image}
                alt="Category"
                className="w-full aspect-[16/9] object-contain bg-gray-300"
              />

              <div className="p-4">
                <h3 className="text-lg font-semibold">
                  {formatCategoryName(value)}
                </h3>
                <p className="text-sm text-gray-600 truncate">{item.link}</p>
                <div className="flex justify-end mt-4 space-x-2">
                  <button
                    className="flex items-center px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    onClick={() => handleDelete(item.id)}
                  >
                    <MdDelete className="mr-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 text-center">
        <button
          className="w-full sm:w-auto bg-green-500 text-white px-6 py-3 rounded shadow hover:bg-green-600"
          onClick={openModal}
        >
          Add {formatCategoryName(value)}
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Add {formatCategoryName(value)}
              </h2>
              <XMarkIcon
                className="h-6 w-6 text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
                onClick={closeModal}
              />
            </div>

            <form id="homeScreenForm">
              {/* Image Upload Section */}
              <div className="space-y-4 mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  {formatCategoryName(value)} Image{" "}
                  <span className="text-red-500">*</span>
                </label>

                {imagePreview ? (
                  <div className="relative">
                    <div className="w-full bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 flex justify-center items-center p-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-auto max-h-96"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                      title="Remove image"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer w-full h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                  >
                    <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
                    <span className="text-gray-600 font-medium">
                      Upload Image
                    </span>
                    <span className="text-gray-400 text-sm mt-1">
                      Click to browse files
                    </span>
                    <span className="text-gray-400 text-xs mt-2">
                      Required: 1600x900 pixels
                    </span>
                  </label>
                )}

                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />

                {imageError && (
                  <p className="text-red-500 text-sm">{imageError}</p>
                )}
                <p className="text-gray-500 text-xs">
                  Image must be exactly 1600x900 pixels and less than 5MB
                </p>
              </div>

              <label className="block mb-2 font-semibold">
                Link <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="link"
                className="w-full mb-4 p-2 border rounded bg-gray-200 h-10"
                placeholder="Enter link..."
                required
              />
            </form>

            <div className="flex justify-end">
              <button
                type="button"
                className="px-4 py-2 bg-gray-300 rounded mr-2"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-green-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                onClick={handleSubmit}
                disabled={!selectedImage}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataManagement;
